/**
 * The SELECT ORDER/SELL QUOTE "Before you continue" items — one definition
 * shared by the section card (SelectQuoteChecklist) and the left rail's
 * current-step mini-checklist (RequestSectionNav), so the two can never
 * disagree about what "done" means. Kept in its own module (not the component
 * file) so fast refresh keeps working.
 *
 * Mirrors sectionConfig's SEND_QUOTE isComplete() in plain language. The
 * selection item surfaces the silent no-op that used to look like a broken
 * Save: with nothing checkmarked there is nothing to persist.
 */
export function getQuoteChecklistItems(request, selectedQuote, isPxSellSlot) {
  const isOrder = request?.request_type === "order";
  // The live checkmark wins; a reloaded page falls back to the request's
  // active quote, which is what the section's completion reads anyway.
  const quote = selectedQuote ?? request?.last_quote ?? null;

  const status =
    typeof quote?.status === "string" ? quote.status.toLowerCase() : quote?.status;
  const isAccepted = status === "accepted_by_client";
  const isSent = status === "quote_sent" || !!quote?.quote_send_type || isAccepted;
  const hasListing = !!(quote?.listing_id || request?.linked_listing_id);

  return [
    {
      key: "selected",
      done: !!quote?.id,
      label: "Approved quote selected",
      detail: quote?.id
        ? "The checkmarked quote below is the one this deal will use."
        : "Tick the quote the client accepted in the list below — nothing saves without a selection.",
    },
    {
      key: "sent",
      done: isSent,
      label: "Quote sent to the client",
      or: "or accepted",
      detail: isSent
        ? isAccepted
          ? "Done — the client accepted this quote."
          : "Done — the quote has been sent. Marking it accepted by the client also counts."
        : "Not yet — send the quote, or mark it accepted if the client already agreed.",
    },
    isOrder && !isPxSellSlot
      ? {
          key: "listing",
          done: hasListing,
          label: "Listing created for this quote",
          detail: hasListing
            ? "Done — the quote is linked to a listing."
            : "Not yet — use Create listing on the selected quote so the order can move to payment.",
        }
      : {
          key: "accepted",
          done: isAccepted,
          label: "Marked accepted by client",
          detail: isAccepted
            ? "Done — the deal can move on."
            : "Not yet — mark the quote accepted once the client agrees. The listing is published later, not here.",
        },
  ];
}
